import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PaymentButton from "@/components/PaymentButton";
import PayPalPayButton from "@/components/PayPalPayButton";
import { ArrowUpRight, Calendar, MapPin, Sparkles, ShieldCheck } from "lucide-react";
import { trackEvent, getStoredAttribution, makeEventId, metaTrack } from "@/lib/analytics";
import { usePageMeta } from "@/lib/pageMeta";

/* Deep-green / emerald / cream Climate Power Summit visual identity */
const CPS_STYLE = {
    "--cps-forest": "#0F2A22",
    "--cps-forest-2": "#143A2C",
    "--cps-emerald": "#1F6E4C",
    "--cps-lime": "#A7C957",
    "--cps-cream": "#F5EFD8",
    "--cps-cream-dim": "rgba(245,239,216,0.72)",
    "--cps-border": "rgba(245,239,216,0.18)",
};

const HAWA_MAHAL_IMG =
    "https://images.unsplash.com/photo-1757310062384-d3bcfe3026e8?w=1600&q=80&fm=jpg&fit=crop";

const roles = [
    "Student",
    "Young Professional",
    "Educator",
    "Entrepreneur",
    "Researcher",
    "NGO Leader",
    "Corporate Professional",
    "Government Official",
];

const OPPORTUNITIES = [
    { count: "10", label: "Fully Funded" },
    { count: "40", label: "Partially Funded" },
    { count: "20", label: "Self-Funded" },
];

/**
 * Detect whether the visitor is likely in India.
 * We use browser timezone as a lightweight signal (no third-party geo call).
 * Users can still toggle manually — this only sets the default.
 */
function detectDefaultRegion() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        if (/^Asia\/(Kolkata|Calcutta)$/i.test(tz)) return "india";
    } catch (_e) {
        // ignore
    }
    return "international";
}

export default function CpsApplyPage() {
    const navigate = useNavigate();
    const [region, setRegion] = useState(detectDefaultRegion());

    const priceItem = region === "india" ? "cps-2026-india" : "cps-2026-intl";
    const priceDisplay = region === "india" ? "₹500" : "USD 10";
    const priceSubtitle = region === "india" ? "India · Indian Rupees" : "International · USD";

    const [form, setForm] = useState({
        program_slug: "cps-2026",
        full_name: "",
        email: "",
        phone: "",
        country: region === "india" ? "India" : "",
        role: "Student",
        organization: "",
        motivation: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [applicationId, setApplicationId] = useState(null);
    const [applicationStartFired, setApplicationStartFired] = useState(false);

    // Page-specific SEO / social-preview metadata for /apply/cps-2026.
    usePageMeta({
        title: "Climate Power Summit 2026 | ZSGI",
        description: "Apply for Climate Power Summit 2026 in Jaipur, India.",
        ogTitle: "Climate Power Summit 2026 | ZSGI",
        ogDescription: "Applications are open for Climate Power Summit 2026 in Jaipur, India.",
        twitterTitle: "Climate Power Summit 2026 | ZSGI",
        twitterDescription: "Applications are open for Climate Power Summit 2026 in Jaipur, India.",
        canonical: "https://zsgi.org/apply/cps-2026",
    });

    // CPS_Landing_Page_View — fires exactly once when the visitor lands on
    // the CPS apply funnel. This is the top of the conversion funnel.
    // Also fires the Meta standard ViewContent event (dedup-guarded per tab).
    useEffect(() => {
        trackEvent("CPS_Landing_Page_View", { program_slug: "cps-2026" });
        metaTrack("ViewContent", {
            content_ids: ["cps-2026"],
            content_name: "Climate Power Summit 2026 — Application",
            content_category: "cps-2026-application",
            content_type: "product",
        }, { dedupKey: "ViewContent:cps-2026-apply" });
    }, []);

    // Keep country in sync when region toggle changes (only if user hasn't typed one)
    useEffect(() => {
        setForm((f) => {
            if (region === "india" && (!f.country || f.country === "")) return { ...f, country: "India" };
            return f;
        });
    }, [region]);

    const change = (k) => (e) => {
        // CPS_Application_Start — fires once, on the first keystroke in any form field.
        if (!applicationStartFired) {
            setApplicationStartFired(true);
            trackEvent("CPS_Application_Start", { program_slug: "cps-2026" });
        }
        setForm({ ...form, [k]: e.target.value });
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const attribution = getStoredAttribution();
            const { data } = await api.post("/applications", { ...form, attribution });
            setApplicationId(data.id);
            // CPS_Verification_Complete — application record persisted to DB.
            // Post-OTP-removal this is the "verified applicant" step now.
            trackEvent("CPS_Verification_Complete", {
                program_slug: "cps-2026",
                application_id: data.id,
                email: form.email,
                phone: form.phone,
                full_name: form.full_name,
                country: form.country,
            });
            // Meta standard: CompleteRegistration — application saved, before payment.
            // No PII in Meta params (advanced matching handled via CAPI server-side).
            metaTrack("CompleteRegistration", {
                content_category: "cps-2026-application",
                content_name: "Climate Power Summit 2026 — Application Submitted",
                content_ids: ["cps-2026"],
                currency: region === "india" ? "INR" : "USD",
                value: region === "india" ? 500 : 10,
            }, { dedupKey: `CompleteRegistration:${data.id}` });
            toast.success("Application saved — complete payment to confirm");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const paidEventIdRef = useState(() => makeEventId("cps-paid"))[0];

    const firePaymentInitiated = () => {
        const value = region === "india" ? 500 : 10;
        const currency = region === "india" ? "INR" : "USD";
        trackEvent("CPS_Payment_Initiated", {
            program_slug: "cps-2026",
            application_id: applicationId,
            value,
            currency,
            email: form.email,
            phone: form.phone,
            full_name: form.full_name,
            country: form.country,
        });
        // Meta standard: InitiateCheckout — user actually clicked pay.
        // dedupKey allows re-fire per application (in case user retries payment)
        // but not on accidental double-click within the same second.
        metaTrack("InitiateCheckout", {
            content_ids: [priceItem],
            content_category: "cps-2026-application",
            content_name: "Climate Power Summit 2026 — Application Fee",
            content_type: "product",
            currency,
            value,
            num_items: 1,
        });
    };

    const onPaid = () => {
        // CPS_Payment_Success + CPS_Application_Complete — the PRIMARY paid
        // advertising conversion. Same event_id as the server-side CAPI fire
        // (razorpay/verify or paypal/capture) so Meta dedups the two.
        const value = region === "india" ? 500 : 10;
        const currency = region === "india" ? "INR" : "USD";
        const eventId = paidEventIdRef;
        const commonParams = {
            program_slug: "cps-2026",
            application_id: applicationId,
            value,
            currency,
            transaction_id: applicationId,
            email: form.email,
            phone: form.phone,
            full_name: form.full_name,
            country: form.country,
        };
        trackEvent("CPS_Payment_Success", { ...commonParams, event_id: `${eventId}-success` });
        trackEvent("CPS_Application_Complete", { ...commonParams, event_id: `${eventId}-complete` });
        // Meta standard: Purchase.
        //
        // Fires ONLY here — after the backend has independently verified the
        // Razorpay signature (via /payments/verify) or successfully captured
        // the PayPal order (via /payments/paypal/capture). Never on mere
        // arrival at the payment page nor on return from the payment provider.
        //
        // event_id is application-scoped so:
        //   1. Any refresh cannot mint a second, differently-ided Purchase.
        //   2. This browser fire is deduped against the server-side CAPI fire
        //      from /payments/verify or /payments/paypal/capture (identical id).
        //   3. The confirmation-page fallback fire (see CpsConfirmationPage)
        //      uses the same id so it never causes a duplicate.
        const purchaseEventId = applicationId ? `cps-purchase-${applicationId}` : `${eventId}-purchase`;
        const purchaseParams = {
            content_ids: [priceItem],
            content_category: "cps-2026-application",
            content_name: "Climate Power Summit 2026 — Application Fee",
            content_type: "product",
            currency,
            value,
            num_items: 1,
        };
        // Persist a "pending purchase" record BEFORE the fbq call. If the fbq
        // beacon fails to send (unmount race, ad-blocker, network drop), the
        // confirmation page will flush this record on mount. sessionStorage
        // is per-tab so a hard reload in a new tab cannot replay it.
        try {
            sessionStorage.setItem(
                "zsgi.pending_purchase.v1",
                JSON.stringify({
                    event_id: purchaseEventId,
                    application_id: applicationId,
                    params: purchaseParams,
                    verified_at: Date.now(),
                })
            );
        } catch (_e) { /* noop */ }
        // Primary browser fire.
        metaTrack("Purchase", purchaseParams, {
            dedupKey: `Purchase:${applicationId || eventId}`,
            eventId: purchaseEventId,
        });
        // Redirect to CPS-branded confirmation page
        navigate(`/apply/cps-2026/received?app=${applicationId || ""}`, { replace: true });
    };

    const isPayStage = !!applicationId;
    const showPayPal = region === "international";

    return (
        <section
            data-testid="cps-apply-page"
            style={CPS_STYLE}
            className="relative min-h-screen pt-28 md:pt-36 pb-24 md:pb-40"
        >
            {/* Layered green background with Jaipur imagery */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, var(--cps-forest) 0%, var(--cps-forest-2) 40%, var(--cps-forest) 100%)" }} />
            <div className="absolute inset-x-0 top-0 h-[70vh] opacity-[0.18]">
                <img
                    src={HAWA_MAHAL_IMG}
                    alt="Hawa Mahal, Jaipur"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, var(--cps-forest) 90%)" }} />
            </div>
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 15% 15%, var(--cps-lime) 0, transparent 40%), radial-gradient(circle at 85% 85%, var(--cps-emerald) 0, transparent 50%)" }} />

            <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                {/* CPS Header */}
                <div className="grid md:grid-cols-12 gap-10 mb-10 md:mb-14">
                    <div className="md:col-span-7">
                        <div className="label-mono flex items-center gap-2" style={{ color: "var(--cps-lime)" }} data-testid="cps-apply-eyebrow">
                            <Sparkles className="w-3 h-3" />
                            CLIMATE POWER SUMMIT 2026 · APPLICATIONS OPEN
                        </div>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
                            className="serif-display text-5xl md:text-7xl mt-4 leading-[0.95]"
                            style={{ color: "var(--cps-cream)" }}
                        >
                            Apply for{" "}
                            <span className="serif-italic" style={{ color: "var(--cps-lime)" }}>
                                Climate Power Summit 2026
                            </span>
                            .
                        </motion.h1>
                        <p className="mt-5 serif-italic text-xl md:text-2xl" style={{ color: "var(--cps-lime)" }}>
                            People • Planet • Progress
                        </p>
                        <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                            <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }}>
                                <MapPin className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} /> Jaipur, India
                            </div>
                            <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }}>
                                <Calendar className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} /> 13–15 November 2026
                            </div>
                        </div>

                        {/* Opportunities panel */}
                        <div
                            className="mt-10 grid grid-cols-3 border"
                            style={{ borderColor: "var(--cps-border)" }}
                            data-testid="cps-apply-opportunities"
                        >
                            {OPPORTUNITIES.map((op, i) => (
                                <div
                                    key={op.label}
                                    className={`p-5 md:p-6 ${i < 2 ? "border-r" : ""}`}
                                    style={{ borderColor: "var(--cps-border)" }}
                                    data-testid={`cps-apply-opp-${op.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div className="serif-display text-3xl md:text-4xl" style={{ color: "var(--cps-lime)" }}>{op.count}</div>
                                    <div className="label-mono text-[10px] mt-1" style={{ color: "var(--cps-cream-dim)" }}>{op.label}</div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-3 label-mono text-[11px]" style={{ color: "var(--cps-cream-dim)" }}>
                            Funding category is assigned after application review
                        </p>
                    </div>

                    {/* Fee card */}
                    <div className="md:col-span-5 md:pt-10">
                        <aside
                            data-testid="cps-fee-card"
                            className="p-6 md:p-8"
                            style={{ background: "rgba(15,42,34,0.6)", border: "1px solid var(--cps-border)", backdropFilter: "blur(12px)" }}
                        >
                            <div className="label-mono" style={{ color: "var(--cps-lime)" }}>— APPLICATION FEE</div>

                            {/* Region toggle */}
                            <div
                                className="mt-4 grid grid-cols-2 gap-0 border"
                                style={{ borderColor: "var(--cps-border)" }}
                                role="tablist"
                                aria-label="Select your region"
                            >
                                <button
                                    type="button"
                                    onClick={() => setRegion("india")}
                                    data-testid="cps-region-india"
                                    className="py-3 label-mono text-[11px] transition-colors"
                                    style={{
                                        background: region === "india" ? "var(--cps-lime)" : "transparent",
                                        color: region === "india" ? "var(--cps-forest)" : "var(--cps-cream)",
                                    }}
                                >
                                    India · ₹500
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRegion("international")}
                                    data-testid="cps-region-intl"
                                    className="py-3 label-mono text-[11px] transition-colors"
                                    style={{
                                        background: region === "international" ? "var(--cps-lime)" : "transparent",
                                        color: region === "international" ? "var(--cps-forest)" : "var(--cps-cream)",
                                        borderLeft: "1px solid var(--cps-border)",
                                    }}
                                >
                                    International · USD 10
                                </button>
                            </div>

                            <div className="mt-8">
                                <div className="label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>YOU PAY</div>
                                <div
                                    className="serif-display text-5xl md:text-6xl leading-none mt-2"
                                    style={{ color: "var(--cps-lime)" }}
                                    data-testid="cps-price-display"
                                >
                                    {priceDisplay}
                                </div>
                                <div className="label-mono text-[10px] mt-2" style={{ color: "var(--cps-cream-dim)" }}>
                                    {priceSubtitle}
                                </div>
                            </div>

                            <div
                                className="mt-6 py-3 px-4 label-mono text-[11px] flex items-center gap-2"
                                style={{ background: "rgba(167,201,87,0.1)", color: "var(--cps-cream)", border: "1px solid rgba(167,201,87,0.25)" }}
                            >
                                <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} />
                                Secure payment · No refunds after application review begins
                            </div>
                        </aside>

                        {/* Cross-link: direct self-funded registration */}
                        <Link
                            to="/apply/cps-2026/self-funded"
                            className="mt-6 block p-5 border transition-colors hover:bg-[rgba(167,201,87,0.06)]"
                            style={{ borderColor: "var(--cps-border)" }}
                            data-testid="cps-self-funded-crosslink"
                        >
                            <div className="label-mono text-[10px]" style={{ color: "var(--cps-lime)" }}>PREFER TO REGISTER DIRECTLY?</div>
                            <div className="mt-2 flex items-center justify-between gap-4">
                                <span className="text-sm" style={{ color: "var(--cps-cream)" }}>
                                    Self-Funded Delegate — ₹32,000 · confirmed seat, no review or interview
                                </span>
                                <ArrowUpRight className="w-4 h-4 flex-shrink-0" style={{ color: "var(--cps-lime)" }} />
                            </div>
                        </Link>
                    </div>
                </div>

                {/* Two-column: application form / payment */}
                <div className="grid md:grid-cols-12 gap-10 mt-8">
                    <div className="md:col-span-7">
                        {!isPayStage ? (
                            <form
                                onSubmit={submit}
                                className="space-y-8"
                                data-testid="cps-apply-form"
                            >
                                <div>
                                    <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Full name</label>
                                    <input required className="input-editorial" value={form.full_name} onChange={change("full_name")} data-testid="cps-apply-name" />
                                </div>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Email</label>
                                        <input required type="email" className="input-editorial" value={form.email} onChange={change("email")} data-testid="cps-apply-email" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Phone</label>
                                        <input required className="input-editorial" value={form.phone} onChange={change("phone")} data-testid="cps-apply-phone" placeholder={region === "india" ? "+91…" : "+…"} />
                                    </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Country</label>
                                        <input required className="input-editorial" value={form.country} onChange={change("country")} data-testid="cps-apply-country" />
                                    </div>
                                    <div>
                                        <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Role</label>
                                        <select className="input-editorial" value={form.role} onChange={change("role")} data-testid="cps-apply-role">
                                            {roles.map((r) => <option key={r} value={r} className="bg-[var(--cps-forest)]">{r}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>Organization (optional)</label>
                                    <input className="input-editorial" value={form.organization} onChange={change("organization")} data-testid="cps-apply-organization" />
                                </div>
                                <div>
                                    <label className="label-mono block mb-2" style={{ color: "var(--cps-lime)" }}>
                                        Why do you want to attend Climate Power Summit 2026?
                                    </label>
                                    <textarea
                                        required
                                        rows={4}
                                        className="input-editorial resize-none"
                                        value={form.motivation}
                                        onChange={change("motivation")}
                                        data-testid="cps-apply-motivation"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="cps-apply-submit"
                                    className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] font-medium transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
                                    style={{ background: "var(--cps-lime)", color: "var(--cps-forest)" }}
                                >
                                    {submitting ? "Saving…" : "Continue to Payment"} <ArrowUpRight className="w-4 h-4" />
                                </button>
                            </form>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-8 md:p-10"
                                style={{ background: "rgba(15,42,34,0.55)", border: "1px solid var(--cps-border)" }}
                                data-testid="cps-pay-stage"
                            >
                                <div className="label-mono" style={{ color: "var(--cps-lime)" }}>
                                    — APPLICATION #{applicationId.slice(0, 8).toUpperCase()}
                                </div>
                                <h2
                                    className="serif-display text-3xl md:text-4xl mt-4"
                                    style={{ color: "var(--cps-cream)" }}
                                >
                                    Almost there, {form.full_name.split(" ")[0]}.
                                </h2>
                                <p className="mt-3" style={{ color: "var(--cps-cream-dim)" }}>
                                    Complete your application fee to submit your application to the review committee for the Climate Power Summit 2026 in Jaipur.
                                </p>

                                <div className="mt-8 pt-8 border-t" style={{ borderColor: "var(--cps-border)" }}>
                                    <div className="flex items-end justify-between mb-6">
                                        <div>
                                            <div className="label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>Application fee</div>
                                            <div className="serif-display text-4xl md:text-5xl mt-1" style={{ color: "var(--cps-lime)" }}>
                                                {priceDisplay}
                                            </div>
                                            <div className="label-mono text-[10px] mt-1" style={{ color: "var(--cps-cream-dim)" }}>
                                                {priceSubtitle}
                                            </div>
                                        </div>
                                    </div>

                                    <PaymentButton
                                        purpose="program"
                                        item={priceItem}
                                        name={form.full_name}
                                        email={form.email}
                                        phone={form.phone}
                                        application_id={applicationId}
                                        label={`Pay Application Fee · ${priceDisplay}`}
                                        className="inline-flex items-center gap-2 w-full justify-center px-6 py-4 label-mono text-[11px] font-medium transition-all hover:scale-[1.01]"
                                        testid="cps-pay-primary"
                                        onBeforePay={firePaymentInitiated}
                                        onPaid={onPaid}
                                    />

                                    {showPayPal && (
                                        <>
                                            <div className="flex items-center gap-3 label-mono text-[11px] my-5" style={{ color: "var(--cps-cream-dim)" }}>
                                                <span className="flex-1 h-px" style={{ background: "var(--cps-border)" }} /> OR <span className="flex-1 h-px" style={{ background: "var(--cps-border)" }} />
                                            </div>
                                            <PayPalPayButton
                                                purpose="program"
                                                item={priceItem}
                                                name={form.full_name}
                                                email={form.email}
                                                phone={form.phone}
                                                application_id={applicationId}
                                                onBeforePay={firePaymentInitiated}
                                                onPaid={onPaid}
                                            />
                                        </>
                                    )}

                                    <p className="mt-6 label-mono text-[10px]" style={{ color: "var(--cps-cream-dim)" }}>
                                        By continuing you agree that the application fee is non-refundable once review begins. Selection results and funding tier will be shared by email.
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Right rail — summary */}
                    <aside className="md:col-span-5 md:pl-4">
                        <div
                            className="p-6 md:p-8"
                            style={{ background: "rgba(31,110,76,0.12)", border: "1px solid var(--cps-border)" }}
                            data-testid="cps-summary-card"
                        >
                            <div className="label-mono" style={{ color: "var(--cps-lime)" }}>— YOU'RE APPLYING TO</div>
                            <h3 className="serif-display text-2xl md:text-3xl mt-3" style={{ color: "var(--cps-cream)" }}>
                                Climate Power Summit 2026
                            </h3>
                            <p className="mt-3 serif-italic" style={{ color: "var(--cps-lime)" }}>People • Planet • Progress</p>
                            <ul className="mt-6 space-y-3" style={{ color: "var(--cps-cream-dim)" }}>
                                <li className="flex items-start gap-3">
                                    <span className="w-1.5 h-1.5 mt-2" style={{ background: "var(--cps-lime)" }} />
                                    <span>Jaipur, India · 13–15 November 2026</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-1.5 h-1.5 mt-2" style={{ background: "var(--cps-lime)" }} />
                                    <span>10 Fully Funded · 40 Partially Funded · 20 Self-Funded seats</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-1.5 h-1.5 mt-2" style={{ background: "var(--cps-lime)" }} />
                                    <span>Funding tier assigned after application review</span>
                                </li>
                                <li className="flex items-start gap-3">
                                    <span className="w-1.5 h-1.5 mt-2" style={{ background: "var(--cps-lime)" }} />
                                    <span>Hosted by Z Ed School of Global Impact</span>
                                </li>
                            </ul>

                            <div className="mt-8 pt-6 border-t" style={{ borderColor: "var(--cps-border)" }}>
                                <Link
                                    to="/programs/cps-2026"
                                    data-testid="cps-explore-link"
                                    className="label-mono text-[11px] inline-flex items-center gap-1 underline underline-offset-4"
                                    style={{ color: "var(--cps-lime)" }}
                                >
                                    Explore full summit programme <ArrowUpRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
}
